import React, { useState, useEffect } from 'react';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';

import { Button, ButtonTheme } from '@apply/components';

import { SigningFontModal, Font } from './SigningFontModal';
import * as Store from '@apply/store';
import PdfIFrameViewer from '../viewers/PdfIFrameViewer';
import { isMobileScreen } from '../helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { fonts } from './SigningFontModal';
import './esignnoticemodal.scss';

export interface EsignNoticeModalProps {
    isOpen: boolean;
    onSignCallback?: (fontname: Font) => void;
    onCloseCallback?: () => void;
    signatureText?: string;
    notice?: Store.UserStore.Types.UserNotice;
    noticeType?: string;
}

export const EsignNoticeModal: React.FC<EsignNoticeModalProps> = ({
    isOpen,
    onSignCallback,
    onCloseCallback,
    signatureText,
    notice,
    noticeType,
}) => {
    const [openSigningFontModal, setOpenSigningFontModal] = useState(false);
    const [signatureFont, setSignatureFont] = useState<Font>(undefined);
    const [signed, setSigned] = useState<boolean>(false);
    const [preSigned, set_preSigned] = useState<boolean>(false);
    const [isMobile, setIsMobile] = useState<boolean>(false);

    useEffect(() => {
        setIsMobile(isMobileScreen());
    }, []);

    useEffect(() => {
        if (notice?.eSignDate) {
            setSigned(true);
            set_preSigned(true);
        } else {
            setSigned(false);
            set_preSigned(false);
        }

        setSignatureFont(fonts[0]);
    }, [notice]);

    useEffect(() => {
        // Lock body scroll when modal is open (especially important for mobile)
        if (isOpen && isMobile) {
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';

            return () => {
                // Restore scroll position
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
                window.scrollTo(0, scrollY);
            };
        }
    }, [isOpen, isMobile]);

    const handleClose = () => {
        setSignatureFont(undefined);
        onCloseCallback();
    };

    const onClickToSign = (event) => {
        event.preventDefault();

        onSignCallback(signatureFont);
    };

    const handleDownload = () => {
        if (notice?.signedFile?.url) {
            window.open(notice.signedFile.url, '_blank');
        } else if (notice?.noticeFile?.url) {
            window.open(notice.noticeFile.url, '_blank');
        } else {
            console.error('No file URL available for download.');
        }
    };

    const handleSigningConfirm = (font) => {
        setSignatureFont(font);
        setSigned(true);
    };

    const handleSigningCancel = () => {
        setOpenSigningFontModal(false);
    };

    if (!isOpen || !notice) {
        // Ensure modal doesn't render if not open or no notice
        return null;
    }

    const MobileComponent: React.FC = () => {
        return (
            <div className='mobile-modal-overlay'>
                <div className='mobile-modal-container'>
                    <div className='mobile-modal-header'>
                        <h1 className='mobile-modal-title'>{noticeType}</h1>
                        <button
                            className='mobile-modal-close'
                            onClick={handleClose}
                            aria-label='Close'
                        >
                            &times;
                        </button>
                    </div>
                    <div className='mobile-modal-content-area'>
                        <PdfIFrameViewer
                            title='signNoticeIFrameMobile'
                            url={notice.noticeFile.url}
                            className='mobile-modal-iframe'
                            height='500px'
                            width='100%'
                            viewerOptions={{ fitWidth: true }}
                        />
                        {!preSigned && notice.eSignTypeId !== Store.UserStore.Types.ESignType.NotRequired && (
                            <div className='mobile-modal-signature-section'>
                                <label className='mobile-modal-signed-by-label'>
                                    <strong>Signed by:</strong>
                                </label>
                                <div
                                    className='mobile-modal-signature-display'
                                    style={{
                                        fontFamily: signed ? signatureFont?.name : 'inherit',
                                    }}
                                >
                                    {signed ? (
                                        signatureText
                                    ) : (
                                        <div className='esign-notice-modal__mobile-sign-button'>
                                            <Button
                                                id='click-sign-mobile'
                                                key='click-to-sign-mobile'
                                                width='200px'
                                                theme={ButtonTheme.Primary}
                                                handleClick={(e) => onClickToSign(e)}
                                            >
                                                Click to Sign
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {signed && signatureText && (
                                    <label className='mobile-modal-printed-name-label'>
                                        <strong>{signatureText}</strong>
                                    </label>
                                )}
                            </div>
                        )}
                        {preSigned && signatureText && notice.eSignTypeId !== Store.UserStore.Types.ESignType.NotRequired && (
                            <div className='mobile-modal-signature-section'>
                                <label className='mobile-modal-signed-by-label'>
                                    <strong>Signed by:</strong>
                                </label>
                                <div
                                    className='mobile-modal-signature-display'
                                    style={{
                                        fontFamily: signatureFont?.name,
                                    }}
                                >
                                    {signatureText}
                                </div>
                                <label className='mobile-modal-printed-name-label'>
                                    <strong>{signatureText}</strong>
                                </label>
                            </div>
                        )}
                    </div>
                    <div className='mobile-modal-actions-container'>
                        <Button
                            id='download-button-mobile'
                            key='download-button-mobile'
                            theme={ButtonTheme.Tertiary}
                            handleClick={() => handleDownload()}
                        >
                            Download
                        </Button>
                        {preSigned && (
                            <div className='mobile-modal-signed-indicator'>
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    className='mobile-modal-signed-indicator-icon'
                                />
                                <span className='mobile-modal-signed-indicator-text'>Document Signed</span>
                            </div>
                        )}
                    </div>
                </div>
                <SigningFontModal
                    isOpen={openSigningFontModal}
                    signatureText={signatureText}
                    confirmCallback={handleSigningConfirm}
                    cancelCallback={handleSigningCancel}
                />
            </div>
        );
    };

    const DesktopComponent: React.FC = () => {
        return (
            <div>
                <Dialog
                    open={isOpen}
                    aria-labelledby='alert-dialog-title'
                    aria-describedby='alert-dialog-description'
                    maxWidth={false}
                    fullWidth={true}
                    PaperProps={{ className: 'esign-notice-modal__paper' }}
                >
                    <DialogContent>
                        <div className='modal-container'>
                            <div className='modal-backdrop'></div>
                            <div
                                className='modal'
                                role='dialog'
                            >
                                <div
                                    className='modal-dialog'
                                    role='document'
                                >
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1>User Notice</h1>
                                        <button
                                            type='button'
                                            className='close esign-notice-modal__close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={handleClose}
                                            title='Close'
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>
                                    <PdfIFrameViewer
                                        title='signNoticeIFrame'
                                        url={notice.noticeFile.url}
                                        height='500px'
                                        width='100%'
                                        viewerOptions={{ fitWidth: true, hideToolbar: true }}
                                    />
                                    <div className='modal-content modal-content--esign'>
                                        <div className='modal-body'>
                                            <div className='standard-form esign-notice-modal__form'>
                                                {!preSigned &&
                                                    notice.eSignTypeId !== Store.UserStore.Types.ESignType.NotRequired && (
                                                        <div className='esign-notice-modal__signature-row'>
                                                            <div className='esign-notice-modal__signature-container'>
                                                                <div>
                                                                    <label>
                                                                        <strong>Signed by:</strong>
                                                                    </label>
                                                                </div>
                                                                <div className='esign-notice-modal__signature-value'>
                                                                    {signed ? (
                                                                        <div
                                                                            className='esign-notice-modal__signature-text'
                                                                            style={{
                                                                                fontFamily: signatureFont?.name,
                                                                            }}
                                                                        >
                                                                            {signatureText}
                                                                        </div>
                                                                    ) : (
                                                                        <Button
                                                                            id='click-sign'
                                                                            key='click-to-sign'
                                                                            width='200px'
                                                                            theme={ButtonTheme.Primary}
                                                                            handleClick={(e) => onClickToSign(e)}
                                                                        >
                                                                            Click to Sign
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <label>
                                                                        <strong>{signatureText}</strong>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions></DialogActions>
                </Dialog>
                <SigningFontModal
                    isOpen={openSigningFontModal}
                    signatureText={signatureText}
                    confirmCallback={handleSigningConfirm}
                    cancelCallback={handleSigningCancel}
                />
            </div>
        );
    };

    return isMobile ? <MobileComponent /> : <DesktopComponent />;
};
